import React, { useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Paper,
  Typography, Stack, CircularProgress, IconButton
} from '@mui/material';
import { CloudUpload, Close, Analytics, Save } from '@mui/icons-material';
import TradeFormFields from './trades/TradeFormFields';
import { useSettings } from '../contexts/SettingsContext';
import axios from 'axios';

const TradeModal = ({ open, onClose, onSave, initialData = {}, strategies = [], trades = [] }) => {
  const { settings } = useSettings();
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(initialData.imageUrl || '');
  const [screenshotBase64, setScreenshotBase64] = useState(initialData.screenshot || '');
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [formData, setFormData] = useState({
    category: 'stock', symbol: '', tradeDate: new Date().toISOString().split('T')[0],
    tradeTime: new Date().toTimeString().split(' ')[0].slice(0, 5), closeDate: '', closeTime: '',
    entryPrice: '', exitPrice: '', stopLoss: '', direction: 'LONG', setup: '', insight: '',
    notes: '', positionSize: settings?.defaultPositionSize || '', leverage: 1, strategyIds: [], status: 'OPEN',
    tradingUrl: '', tags: settings?.defaultTags || [], rating: 3, exchange: '', ...initialData
  });
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      const reader = new FileReader();
      reader.onloadend = () => setScreenshotBase64(reader.result.split(',')[1]);
      reader.readAsDataURL(file);
    }
  };

  const handleAnalyze = async () => {
    if (!screenshotBase64) return;
    setLoadingAnalysis(true);
    try {
      const token = localStorage.getItem('idToken');
      const res = await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/imagetojson_openai', {
        image_base64: screenshotBase64
      }, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data.extractedTradeData || {};
      setFormData(prev => ({ ...prev, ...data }));
    } catch (error) {
      console.error('Analysis error:', error);
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const handleSave = () => {
    const { entryPrice, stopLoss, exitPrice, positionSize, leverage } = formData;
    const ep = parseFloat(entryPrice), sl = parseFloat(stopLoss), tp = parseFloat(exitPrice);
    const size = parseFloat(positionSize), lev = parseFloat(leverage);
    let metrics = {};
    if (ep && sl && tp && size && lev) {
      const risk = Math.abs(ep - sl), reward = Math.abs(tp - ep), qty = (size * lev) / ep;
      const riskAmt = risk * qty, rewardAmt = reward * qty;
      metrics = {
        riskAmount: riskAmt.toFixed(2), rewardAmount: rewardAmt.toFixed(2),
        quantity: qty.toFixed(2), riskRewardRatio: (rewardAmt / riskAmt).toFixed(2)
      };
    }
    onSave({ ...formData, ...metrics, screenshot: screenshotBase64 });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {initialData.tradeId ? 'Edit Trade' : 'Add Trade'}
        <IconButton onClick={onClose}><Close /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', height: '70vh' }}>
          <Box sx={{ width: '40%', p: 2, borderRight: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" gutterBottom>Screenshot</Typography>
            <Paper variant="outlined" sx={{ p: 2, border: '2px dashed', borderColor: 'primary.main', 
              textAlign: 'center', minHeight: 200, display: 'flex', flexDirection: 'column', 
              justifyContent: 'center', mb: 2 }}>
              <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileSelect} />
              {!previewUrl ? (
                <Stack spacing={2} alignItems="center">
                  <CloudUpload sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Button variant="outlined" onClick={() => fileInputRef.current.click()}>Upload</Button>
                </Stack>
              ) : (
                <Box>
                  <img src={previewUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: 180 }} />
                  <Button onClick={() => fileInputRef.current.click()} sx={{ mt: 1 }}>Change</Button>
                </Box>
              )}
            </Paper>
            <Button onClick={handleAnalyze} disabled={!screenshotBase64 || loadingAnalysis} 
              variant="contained" fullWidth startIcon={loadingAnalysis ? <CircularProgress size={16} /> : <Analytics />}>
              {loadingAnalysis ? 'Analyzing...' : 'AI Analyze'}
            </Button>
          </Box>
          <Box sx={{ width: '60%', p: 2, overflow: 'auto' }}>
            <TradeFormFields values={formData} onChange={(field, value) => 
              setFormData(prev => ({ ...prev, [field]: value }))} strategies={strategies} trades={trades} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" startIcon={<Save />}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TradeModal;